import { useMemo, useState } from 'react';
import { useMutation, useIsFetching } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EVENTS, tag } from '../../behaviors/cms/gtm-inputs';
import { UploadFilesForSignError, UploadFilesForSignResponse, uploadFilesForSign } from './uploadDocumentsService';
import { ContractSigningForm, contractSigningFormSchema } from './schemas';
import { getFirstBackendError } from './errors';
import { ContractSigningKind, ContractSigningKindOptionsMap } from './constants';

export const DEFAULT_FORM_VALUES = {
  clientRUT: '',
  clientEmail: '',
  signers: [],
  documentsToSign: [],
  contractSigningKind: ContractSigningKindOptionsMap.NATURAL_PERSON,
};
export const DEFAULT_NEW_SIGNER_VALUES = { rut: '', email: '' };

type ModalType = 'documentsPreview' | 'error' | null;

export function useUploadDocuments() {
  const [modal, setModal] = useState<ModalType>(null);
  const [previewDocuments, setPreviewDocuments] = useState<File[]>([]);

  const form = useForm<ContractSigningForm>({
    resolver: zodResolver(contractSigningFormSchema),
    defaultValues: {
      ...DEFAULT_FORM_VALUES,
    },
    mode: 'onBlur',
  });

  const {
    mutate: dispatchUploadingRequest,
    reset: resetUploadState,
    isLoading: isLoadingUpload,
    isError: isUploadError,
    error: uploadError,
  } = useMutation<
  UploadFilesForSignResponse,
  UploadFilesForSignError,
  { payload: FormData, contractSigningKind: ContractSigningKind }
  >({
    mutationFn: async ({ payload, contractSigningKind }) => (
      await uploadFilesForSign({ payload, contractSigningKind })
    ).data,
    onSuccess: ({ detail_url }) => {
      window.location.href = detail_url;
    },
    onError: () => {
      setModal('error');
    },
  });

  // Handler for the submit of the main form
  const handleSubmit = (data: ContractSigningForm) => {
    setPreviewDocuments(data.documentsToSign.map((doc) => doc.raw));
    setModal('documentsPreview');
  };

  // Handler to close any modal
  const handleCloseModal = () => {
    setModal(null);
    setPreviewDocuments([]);
    resetUploadState();
  };

  const handleCloseErrorModal = () => {
    handleCloseModal();
    form.reset({ ...form.getValues(), documentsToSign: [] });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  interface BuildFormDataPayload {
    entityRut: string;
    signers: ContractSigningForm['signers'];
    entityEmail?: string;
    contractSigningKind: ContractSigningKind;
  }

  /**
   * Build FormData for both single and multiple sign processes
   * - Always includes entity_rut
   * - If no signers, adds entity_email
   * - If signers, adds representatives
   */
  const buildFormData = ({
    entityRut,
    signers,
    entityEmail,
    contractSigningKind,
  }: BuildFormDataPayload) => {
    const formData = new FormData();
    formData.append('entity_rut', entityRut);

    if (contractSigningKind === ContractSigningKindOptionsMap.NATURAL_PERSON && entityEmail) {
      formData.append('entity_email', entityEmail);
    }

    if (
      contractSigningKind === ContractSigningKindOptionsMap.JURIDICAL_PERSON
      && signers.length > 0
    ) {
      signers.forEach(({ rut, email }, i) => {
        formData.append(`representatives[${i}]representative_rut`, String(rut));
        formData.append(`representatives[${i}]representative_email`, String(email));
        formData.append(`representatives[${i}]signing_order`, String(i + 1));
      });
    }

    return formData;
  };

  const appendFilesToFormData = (formData: FormData, documentsToSign: ContractSigningForm['documentsToSign']) => {
    documentsToSign.forEach(({ raw, document_type_id }, index) => {
      formData.append(`documents_to_sign[${index}]file`, raw);
      formData.append(`documents_to_sign[${index}]document_type_id`, document_type_id.toString());
    });
  };

  const handleSendDocuments = async () => {
    const signers = form.getValues('signers');
    const entityRut = form.getValues('clientRUT');
    const entityEmail = form.getValues('clientEmail');
    const documentsToSign = form.getValues('documentsToSign');
    const contractSigningKind = form.getValues('contractSigningKind');

    const payload = buildFormData({
      entityRut,
      signers,
      entityEmail,
      contractSigningKind,
    });
    appendFilesToFormData(payload, documentsToSign);

    tag({
      event: EVENTS.SUBMIT,
      variables: { id: 'submit_doc' },
    });
    dispatchUploadingRequest({ payload, contractSigningKind });
  };

  const { clientRUT, documentsToSign } = form.watch();
  const { formState: { errors } } = form;
  const pendingValidations = useIsFetching({ queryKey: ['document-validation'] });
  const isPreviewButtonDisabled = useMemo(
    () => {
      const clientRutDoesNotExist = !clientRUT;
      const documentsToSignDoesNotExist = documentsToSign.length === 0;
      const documentsToSignHasErrors = !!errors.documentsToSign;
      const isValidatingDocuments = pendingValidations > 0;

      return (
        clientRutDoesNotExist
        || documentsToSignDoesNotExist
        || documentsToSignHasErrors
        || isValidatingDocuments);
    },
    [clientRUT, documentsToSign, errors.documentsToSign, pendingValidations],
  );

  return {
    form,
    handleSubmit,
    modal,
    setModal,
    previewDocuments,
    handleCloseModal,
    handleCloseErrorModal,
    handleSendDocuments,
    descriptiveError: getFirstBackendError(uploadError?.response?.data?.context?.data),
    isLoadingUpload,
    isUploadError,
    isPreviewButtonDisabled,
    uploadError,
  };
}
